import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  ApiSuccessEnvelope,
  OperatorAffiliateDetail,
} from "../../types/affiliates";
import { getPrivateInstance } from "../_privateInstance";

interface AffiliateDetailRange {
  start_date?: string;
  end_date?: string;
}

const fetchAffiliateById = async (
  affiliateId: string,
  range?: AffiliateDetailRange,
): Promise<OperatorAffiliateDetail> => {
  const path = API_ROUTES.GET_AFFILIATE_BY_ID.replace(
    "{affiliate_id}",
    encodeURIComponent(affiliateId),
  );

  // BE enforces both-or-neither on the date range (422
  // AFFILIATE_RETRIEVE_DATE_RANGE_INCOMPLETE otherwise), so only send the
  // params when both bounds are present.
  const params: Record<string, string> = {};
  if (range?.start_date && range?.end_date) {
    params.start_date = range.start_date;
    params.end_date = range.end_date;
  }

  const response = await getPrivateInstance().get<
    ApiSuccessEnvelope<OperatorAffiliateDetail>
  >(path, { params });
  return response.data.data;
};

export const useAffiliate = (
  affiliateId: string | undefined,
  range?: AffiliateDetailRange,
): UseQueryResult<OperatorAffiliateDetail, Error> =>
  useQuery<OperatorAffiliateDetail, Error>({
    // A date-less (or partial) fetch keeps its original 3-segment key so its
    // cache + invalidations are unchanged; only a complete range (the KPI
    // cards) gets its own date-scoped entry — matching the both-or-neither
    // contract BE enforces on the query params.
    queryKey:
      range?.start_date && range?.end_date
        ? [
            "affiliates",
            "detail",
            affiliateId ?? "",
            range.start_date,
            range.end_date,
          ]
        : ["affiliates", "detail", affiliateId ?? ""],
    queryFn: () => fetchAffiliateById(affiliateId as string, range),
    enabled: !!affiliateId,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
